import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface ContratistaData {
	antiguedadRamo: string
	facturacionAnual: string
	cantidadEmpleados: string
	experienciaSiniestral: string
	montoContrato: string
	empleadosAsignados: string
	bienesBajoCustodia: string
	trabajosSimilares: string
	subcontratistas: string
	esPetrolero: boolean
	categoriaPetrolera: string
	aseguradoraActual: string
	vigenciaPoliza: string
	coberturasSumas: string
}

interface RCContratistasContratistaScreenProps {
	onNext: (data: ContratistaData) => void
	onBack: () => void
	data?: Partial<ContratistaData>
}

export function RCContratistasContratistaScreen({
	onNext,
	onBack,
	data,
}: RCContratistasContratistaScreenProps) {
	const [formData, setFormData] = useState<ContratistaData>({
		antiguedadRamo: data?.antiguedadRamo || '',
		facturacionAnual: data?.facturacionAnual || '',
		cantidadEmpleados: data?.cantidadEmpleados || '',
		experienciaSiniestral: data?.experienciaSiniestral || '',
		montoContrato: data?.montoContrato || '',
		empleadosAsignados: data?.empleadosAsignados || '',
		bienesBajoCustodia: data?.bienesBajoCustodia || '',
		trabajosSimilares: data?.trabajosSimilares || '',
		subcontratistas: data?.subcontratistas || '',
		esPetrolero: data?.esPetrolero || false,
		categoriaPetrolera: data?.categoriaPetrolera || '',
		aseguradoraActual: data?.aseguradoraActual || '',
		vigenciaPoliza: data?.vigenciaPoliza || '',
		coberturasSumas: data?.coberturasSumas || '',
	})

	const handleSubmit = () => {
		onNext(formData)
	}

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-6">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Información del Contratista
					</h2>
					<p className="text-center text-sm text-neutral-600">
						Datos sobre el contratista y el trabajo específico. Todos los campos
						son opcionales.
					</p>

					<div className="flex w-full flex-col gap-4">
						<div className="grid grid-cols-2 gap-4">
							<InputText
								label="Antigüedad en el Ramo"
								labelPosition="internal"
								size="l"
								placeholder="Ej: 10 años"
								value={formData.antiguedadRamo}
								onChange={(e) =>
									setFormData({ ...formData, antiguedadRamo: e.target.value })
								}
							/>

							<InputText
								label="Facturación Anual"
								labelPosition="internal"
								size="l"
								placeholder="Ej: $50.000.000"
								value={formData.facturacionAnual}
								onChange={(e) =>
									setFormData({ ...formData, facturacionAnual: e.target.value })
								}
							/>
						</div>

						<InputText
							label="Empleados y Sueldos Anuales"
							labelPosition="internal"
							size="l"
							placeholder="Ej: 25 empleados, $15.000.000 en sueldos"
							value={formData.cantidadEmpleados}
							onChange={(e) =>
								setFormData({ ...formData, cantidadEmpleados: e.target.value })
							}
						/>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Experiencia Siniestral
							</label>
							<p className="text-xs text-neutral-500">
								Reclamos recibidos, siniestros pagados, casos en juicio
							</p>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								rows={3}
								placeholder="Ej: Sin siniestros en los últimos 5 años / 1 reclamo menor en 2023"
								value={formData.experienciaSiniestral}
								onChange={(e) =>
									setFormData({
										...formData,
										experienciaSiniestral: e.target.value,
									})
								}
							/>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<InputText
								label="Monto del Contrato"
								labelPosition="internal"
								size="l"
								placeholder="Ej: $100.000.000"
								value={formData.montoContrato}
								onChange={(e) =>
									setFormData({ ...formData, montoContrato: e.target.value })
								}
							/>

							<InputText
								label="Empleados Asignados"
								labelPosition="internal"
								size="l"
								placeholder="Ej: 15 personas"
								value={formData.empleadosAsignados}
								onChange={(e) =>
									setFormData({
										...formData,
										empleadosAsignados: e.target.value,
									})
								}
							/>
						</div>

						<InputText
							label="Bienes bajo Cuidado, Custodia y Control"
							labelPosition="internal"
							size="l"
							placeholder="Ej: Maquinaria del comitente, materiales de obra"
							value={formData.bienesBajoCustodia}
							onChange={(e) =>
								setFormData({ ...formData, bienesBajoCustodia: e.target.value })
							}
						/>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Trabajos Similares Realizados (último año)
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								rows={2}
								placeholder="Describa trabajos similares completados recientemente"
								value={formData.trabajosSimilares}
								onChange={(e) =>
									setFormData({
										...formData,
										trabajosSimilares: e.target.value,
									})
								}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Subcontratistas
							</label>
							<p className="text-xs text-neutral-500">
								Nombre, si tienen seguro propio y suma asegurada
							</p>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								rows={3}
								placeholder="Ej: Electricista SA - Seguro propio $10M / Plomería SRL - Sin seguro, incluir en póliza"
								value={formData.subcontratistas}
								onChange={(e) =>
									setFormData({ ...formData, subcontratistas: e.target.value })
								}
							/>
						</div>

						<div className="mt-4 rounded-lg bg-amber-50 p-4">
							<div className="flex items-center gap-2">
								<input
									type="checkbox"
									id="esPetrolero"
									checked={formData.esPetrolero}
									onChange={(e) =>
										setFormData({ ...formData, esPetrolero: e.target.checked })
									}
									className="size-4 rounded border-neutral-300"
								/>
								<label
									htmlFor="esPetrolero"
									className="text-sm font-medium text-amber-800"
								>
									Trabajo en campos petroleros
								</label>
							</div>
							{formData.esPetrolero && (
								<div className="mt-3">
									<label className="text-sm font-medium text-neutral-700">
										Categoría y Tarea (A: mayor exposición, C: menor)
									</label>
									<textarea
										className="mt-1 w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
										rows={2}
										placeholder="Ej: Categoría B - Mantenimiento de cañerías"
										value={formData.categoriaPetrolera}
										onChange={(e) =>
											setFormData({
												...formData,
												categoriaPetrolera: e.target.value,
											})
										}
									/>
								</div>
							)}
						</div>

						<div className="mt-2 rounded-lg bg-neutral-50 p-4">
							<h3 className="font-parkinsans mb-3 text-base font-medium text-blue-800">
								Antecedentes Comerciales (opcional)
							</h3>
							<div className="flex flex-col gap-3">
								<InputText
									label="Aseguradora Actual"
									labelPosition="internal"
									size="l"
									placeholder="Nombre de la compañía"
									value={formData.aseguradoraActual}
									onChange={(e) =>
										setFormData({
											...formData,
											aseguradoraActual: e.target.value,
										})
									}
								/>
								<InputText
									label="Vigencia de Póliza"
									labelPosition="internal"
									size="l"
									placeholder="Ej: 01/01/2025 - 01/01/2026"
									value={formData.vigenciaPoliza}
									onChange={(e) =>
										setFormData({ ...formData, vigenciaPoliza: e.target.value })
									}
								/>
								<InputText
									label="Coberturas y Sumas Aseguradas"
									labelPosition="internal"
									size="l"
									placeholder="Ej: RC General $50M, RC Cruzada $20M"
									value={formData.coberturasSumas}
									onChange={(e) =>
										setFormData({
											...formData,
											coberturasSumas: e.target.value,
										})
									}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Enviar Cotización
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
